import { useState, useEffect, useRef } from 'react';

type Position = { x: number; y: number };

interface UseDraggableChatOptions {
	isOpen: boolean;
	onToggle?: () => void;
	onClose?: () => void;
	defaultPosition?: Position;
	minHeight?: number;
	minWidth?: number;
	padding?: number;
	leftPadding?: number;
	snapThreshold?: number;
	resizeHandleSize?: number;
}

export const useDraggableChat = (options: UseDraggableChatOptions = { isOpen: false }) => {
	const {
		isOpen,
		onToggle,
		onClose,
		defaultPosition = { x: 60, y: 90 },
		minHeight = 500,
		minWidth = 400,
		padding = 20,
		leftPadding = 47,
		snapThreshold = 300,
		resizeHandleSize = 20
	} = options;

	const [position, setPosition] = useState<Position>(() => {
		const saved = localStorage.getItem('aiChatPosition');
		if (saved) {
			try {
				return JSON.parse(saved);
			} catch {
				return defaultPosition;
			}
		}
		return defaultPosition;
	});

	const [isDragging, setIsDragging] = useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [resizeDirection, setResizeDirection] = useState<string | null>(null);
	const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
	const [resizeStart, setResizeStart] = useState<{
		mouseX: number;
		mouseY: number;
		containerX: number;
		containerY: number;
		width: number;
		height: number;
	} | null>(null);
	const [snapHotspot, setSnapHotspot] = useState<'left' | 'right' | null>(null);
	const [containerWidth, setContainerWidth] = useState<number>(500);
	const containerRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState<number | null>(() => {
		const saved = localStorage.getItem('aiChatWidth');
		return saved ? parseInt(saved, 10) : null;
	});
	const [height, setHeight] = useState<number | null>(() => {
		const saved = localStorage.getItem('aiChatHeight');
		return saved ? parseInt(saved, 10) : null;
	});
	const [previousDimensions, setPreviousDimensions] = useState<{
		width: number | null;
		height: number;
		x: number;
		y: number;
	} | null>(null);

	// Save position to localStorage when it changes
	useEffect(() => {
		localStorage.setItem('aiChatPosition', JSON.stringify(position));
	}, [position]);

	// Save width to localStorage when it changes
	useEffect(() => {
		if (width !== null) {
			localStorage.setItem('aiChatWidth', width.toString());
		}
	}, [width]);

	// Save height to localStorage when it changes
	useEffect(() => {
		if (height !== null) {
			localStorage.setItem('aiChatHeight', height.toString());
		}
	}, [height]);

	// Reset position and size if chat doesn't fit window when opening
	const prevIsOpenRef = useRef(isOpen);
	useEffect(() => {
		// Only check when chat transitions from closed to open
		if (!isOpen || prevIsOpenRef.current === isOpen) {
			prevIsOpenRef.current = isOpen;
			return;
		}

		prevIsOpenRef.current = isOpen;

		// Calculate using saved dimensions
		const currentWidth = width ?? 500;
		const currentHeight = height ?? minHeight;
		const maxX = window.innerWidth - currentWidth - padding;
		const maxY = window.innerHeight - currentHeight - padding;

		// Check if chat window is outside viewport bounds
		const isOutOfBounds =
			position.x < 0 ||
			position.x > maxX ||
			position.y < padding ||
			position.y > maxY ||
			currentWidth > window.innerWidth - padding * 2 ||
			currentHeight > window.innerHeight - padding * 2;

		if (isOutOfBounds) {
			// Calculate available height (viewport height minus padding and position)
			const availableHeight = window.innerHeight - defaultPosition.y - padding;
			const maxAvailableHeight = Math.max(minHeight, availableHeight);

			// Reset to default position and size, with height filling available space
			setPosition(defaultPosition);
			setWidth(null);
			setHeight(maxAvailableHeight);
		}
	}, [isOpen, defaultPosition, minHeight, padding, width, height, position]);

	// Handle keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Don't trigger shortcuts when Shift is held down
			if (e.shiftKey) {
				return;
			}

			const sessionExpireWarningOpen = document.querySelector('.SessionExpireWarning') !== null;

			if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
				if (sessionExpireWarningOpen) return;
				e.preventDefault();
				onToggle?.();
			} else if (e.key === 'Escape' && isOpen) {
				onClose?.();
			} else if ((e.ctrlKey || e.metaKey) && isOpen && containerRef.current) {
				const containerWidth = containerRef.current.offsetWidth || 500;
				const containerHeight = containerRef.current.offsetHeight || 400;

				let newPosition = { ...position };

				switch (e.key) {
					case 'ArrowRight':
						e.preventDefault();
						newPosition = {
							x: window.innerWidth - containerWidth - padding,
							y: position.y
						};
						break;
					case 'ArrowLeft':
						e.preventDefault();
						newPosition = {
							x: leftPadding,
							y: position.y
						};
						break;
					case 'ArrowUp':
						e.preventDefault();
						newPosition = {
							x: position.x,
							y: padding
						};
						break;
					case 'ArrowDown':
						e.preventDefault();
						newPosition = {
							x: position.x,
							y: window.innerHeight - containerHeight - padding
						};
						break;
					default:
						return;
				}

				// Constrain to viewport bounds
				const maxX = window.innerWidth - containerWidth;
				const maxY = window.innerHeight - containerHeight;
				setPosition({
					x: Math.max(0, Math.min(maxX, newPosition.x)),
					y: Math.max(0, Math.min(maxY, newPosition.y))
				});
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, position, onToggle, onClose, padding, leftPadding]);

	// Detect if mouse is in a corner/edge area (only bottom edge can resize height)
	const getResizeDirection = (e: React.MouseEvent<HTMLDivElement>): string | null => {
		if (!containerRef.current) return null;
		const rect = containerRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;
		const containerWidth = rect.width;
		const containerHeight = rect.height;

		const isLeft = x < resizeHandleSize;
		const isRight = x > containerWidth - resizeHandleSize;
		const isBottom = y > containerHeight - resizeHandleSize;

		// Only allow bottom edge and bottom corners to resize (which can change height)
		// Left and right edges can only resize width
		if (isLeft && isBottom) return 'sw';
		if (isRight && isBottom) return 'se';
		if (isLeft) return 'w';
		if (isRight) return 'e';
		if (isBottom) return 's';

		return null;
	};

	// Check if window is maximized
	const isMaximized = () => {
		const maxHeight = window.innerHeight - padding * 2;
		const maxWidth = window.innerWidth - leftPadding - padding;
		const maxY = padding;
		const maxX = leftPadding;
		return (
			height !== null &&
			width !== null &&
			Math.abs(height - maxHeight) < 10 &&
			Math.abs(width - maxWidth) < 10 &&
			Math.abs(position.y - maxY) < 10 &&
			Math.abs(position.x - maxX) < 10
		);
	};

	// Handle drag functionality (for header/middle area)
	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!containerRef.current) return;

		const resizeDir = getResizeDirection(e);
		if (resizeDir) {
			// Start resizing (resizing is still allowed when maximized)
			const rect = containerRef.current.getBoundingClientRect();
			setResizeStart({
				mouseX: e.clientX,
				mouseY: e.clientY,
				containerX: position.x,
				containerY: position.y,
				width: rect.width,
				height: rect.height
			});
			setResizeDirection(resizeDir);
			setIsResizing(true);
			e.preventDefault();
			return;
		}

		// Don't allow dragging when maximized
		if (isMaximized()) {
			return;
		}

		// Start dragging
		const rect = containerRef.current.getBoundingClientRect();
		setDragOffset({
			x: e.clientX - rect.left,
			y: e.clientY - rect.top
		});
		// Cache container width when dragging starts
		setContainerWidth(rect.width);
		setIsDragging(true);
		e.preventDefault();
	};

	// Drag move and snap logic
	useEffect(() => {
		if (!isDragging) return;

		let animationFrameId: number;

		const handleMouseMove = (e: MouseEvent) => {
			if (animationFrameId) {
				cancelAnimationFrame(animationFrameId);
			}

			animationFrameId = requestAnimationFrame(() => {
				const newX = e.clientX - dragOffset.x;
				const newY = e.clientY - dragOffset.y;

				// Use cached container width for better performance
				const containerHeight = containerRef.current?.offsetHeight || 400;
				const maxX = window.innerWidth - containerWidth;
				const maxY = window.innerHeight - containerHeight - padding; // Respect bottom padding
				const minX = 0;
				const minY = padding; // Respect top padding

				const constrainedX = Math.max(minX, Math.min(maxX, newX));

				// Determine which snap hotspot to show
				if (constrainedX < snapThreshold) {
					setSnapHotspot('left');
				} else if (constrainedX > maxX - snapThreshold) {
					setSnapHotspot('right');
				} else {
					setSnapHotspot(null);
				}

				setPosition({
					x: constrainedX,
					y: Math.max(minY, Math.min(maxY, newY))
				});
			});
		};

		const handleMouseUp = (e: MouseEvent) => {
			if (!containerRef.current) {
				setIsDragging(false);
				setSnapHotspot(null);
				return;
			}

			// Calculate final position from mouse event
			const finalX = e.clientX - dragOffset.x;
			const finalY = e.clientY - dragOffset.y;

			// Snap to edges if within threshold
			const containerWidth = containerRef.current.offsetWidth || 500;
			const containerHeight = containerRef.current.offsetHeight || 400;
			const maxX = window.innerWidth - containerWidth;
			const maxY = window.innerHeight - containerHeight - padding; // Respect bottom padding

			// Constrain to viewport bounds first (with padding)
			const constrainedX = Math.max(0, Math.min(maxX, finalX));
			const constrainedY = Math.max(padding, Math.min(maxY, finalY)); // Respect top and bottom padding

			let snappedX = constrainedX;
			let shouldMaximizeHeight = false;

			// Check left edge
			if (constrainedX < snapThreshold) {
				snappedX = leftPadding;
				shouldMaximizeHeight = true;
			}
			// Check right edge
			else if (constrainedX > maxX - snapThreshold) {
				snappedX = maxX - padding;
				shouldMaximizeHeight = true;
			}

			// Maximize height when snapping
			if (shouldMaximizeHeight) {
				const maxHeight = window.innerHeight - padding * 2;
				setPosition({
					x: Math.max(0, Math.min(maxX, snappedX)),
					y: padding
				});
				setHeight(maxHeight);
			} else {
				setPosition({
					x: Math.max(0, Math.min(maxX, snappedX)),
					y: constrainedY
				});
			}

			setIsDragging(false);
			setSnapHotspot(null);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			if (animationFrameId) {
				cancelAnimationFrame(animationFrameId);
			}
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isDragging, dragOffset, containerWidth, padding, snapThreshold, leftPadding]);

	// Resize logic
	useEffect(() => {
		if (!isResizing || !resizeStart || !resizeDirection) return;

		const handleMouseMove = (e: MouseEvent) => {
			if (!containerRef.current) return;

			// Cap mouse coordinates to window bounds when mouse is outside
			const clientX = Math.max(0, Math.min(window.innerWidth, e.clientX));
			const clientY = Math.max(0, Math.min(window.innerHeight, e.clientY));

			const deltaX = clientX - resizeStart.mouseX;
			const deltaY = clientY - resizeStart.mouseY;

			// Start from the initial resize dimensions and position
			let newWidth = resizeStart.width;
			let newHeight = resizeStart.height;
			let newX = resizeStart.containerX;
			let newY = resizeStart.containerY;

			// Handle resize based on direction
			if (resizeDirection.includes('e')) {
				// Resizing from right edge - width increases/decreases, X stays same
				// Constrain width immediately based on current position
				const maxWidthForPosition = window.innerWidth - resizeStart.containerX - padding;
				newWidth = Math.max(minWidth, Math.min(maxWidthForPosition, resizeStart.width + deltaX));
			}
			if (resizeDirection.includes('w')) {
				// Resizing from left edge - width changes, X moves to keep right edge in place
				// Calculate potential new width first
				const potentialWidth = resizeStart.width - deltaX;
				// Constrain width immediately based on maximum allowed (from leftPadding to right edge)
				const maxWidthForLeftResize = window.innerWidth - leftPadding - padding;
				const constrainedWidth = Math.max(minWidth, Math.min(maxWidthForLeftResize, potentialWidth));
				// Calculate new X position to keep right edge in place
				newX = resizeStart.containerX + (resizeStart.width - constrainedWidth);
				newWidth = constrainedWidth;
			}
			// Only allow bottom edge (s) to change height
			if (resizeDirection.includes('s')) {
				newHeight = Math.max(minHeight, resizeStart.height + deltaY);
			}

			// Y position should not change when resizing (only bottom edge resizes height)
			// Keep original Y position
			newY = resizeStart.containerY;

			// Constrain position first
			const maxX = window.innerWidth - newWidth - padding;
			// Use leftPadding for left edge constraint to avoid sidebar
			const minX = resizeDirection.includes('w') ? leftPadding : padding;
			if (newX < minX) {
				// When X is constrained, adjust width to maintain the resize intent
				if (resizeDirection.includes('w')) {
					// Calculate how much we need to reduce width to fit within constraints
					const widthReduction = minX - newX;
					newWidth = Math.max(minWidth, newWidth - widthReduction);
					newX = minX;
				} else {
					newX = minX;
				}
			}
			if (newX > maxX) {
				// When X exceeds max, adjust width to fit
				if (resizeDirection.includes('w')) {
					const widthReduction = newX - maxX;
					newWidth = Math.max(minWidth, newWidth - widthReduction);
					newX = maxX;
				} else {
					newX = maxX;
				}
			}

			// Now constrain dimensions based on final position
			const maxWidth = window.innerWidth - newX - padding;
			const maxHeight = window.innerHeight - newY - padding;
			newWidth = Math.min(newWidth, maxWidth);
			newHeight = Math.min(newHeight, maxHeight);

			setPosition({ x: newX, y: newY });
			setWidth(newWidth);
			// Only set height if resizing from bottom edge (s, sw, se)
			if (resizeDirection.includes('s')) {
				setHeight(newHeight);
			}
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			setResizeDirection(null);
			setResizeStart(null);
		};

		const handleMouseLeave = () => {
			// When mouse leaves window, cap the resize at window boundaries
			if (!containerRef.current) return;

			const currentX = position.x;
			const currentY = position.y;

			// Cap to maximum allowed dimensions
			const maxX = window.innerWidth - minWidth - padding;
			const maxWidth = window.innerWidth - currentX - padding;
			const maxHeight = window.innerHeight - currentY - padding;

			let finalWidth = width ?? containerRef.current.offsetWidth;
			let finalHeight = height ?? containerRef.current.offsetHeight;
			let finalX = currentX;

			// Constrain width
			if (resizeDirection?.includes('e')) {
				finalWidth = Math.min(finalWidth, maxWidth);
			}
			if (resizeDirection?.includes('w')) {
				const constrainedX = Math.max(leftPadding, Math.min(maxX, currentX));
				finalX = constrainedX;
				finalWidth = Math.min(finalWidth, window.innerWidth - finalX - padding);
			}

			// Constrain height
			if (resizeDirection?.includes('s')) {
				finalHeight = Math.min(finalHeight, maxHeight);
			}

			// Ensure minimum dimensions
			finalWidth = Math.max(minWidth, finalWidth);
			finalHeight = Math.max(minHeight, finalHeight);

			setPosition({ x: finalX, y: currentY });
			setWidth(finalWidth);
			if (resizeDirection?.includes('s')) {
				setHeight(finalHeight);
			}
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		window.addEventListener('mouseleave', handleMouseLeave);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
			window.removeEventListener('mouseleave', handleMouseLeave);
		};
	}, [isResizing, resizeStart, resizeDirection, position, width, minWidth, minHeight, padding, leftPadding]);

	// Handle double-click or button click to maximize/minimize width and height
	const handleDoubleClick = () => {
		const maxHeight = window.innerHeight - padding * 2;
		const maxWidth = window.innerWidth - leftPadding - padding;
		const maxY = padding;
		const maxX = leftPadding;

		if (isMaximized()) {
			// Minimize: restore previous dimensions and position
			if (previousDimensions) {
				setWidth(previousDimensions.width);
				setHeight(previousDimensions.height);
				setPosition({ x: previousDimensions.x, y: previousDimensions.y });
				setPreviousDimensions(null);
			} else {
				// Fallback: restore to minHeight and default width
				setHeight(minHeight);
				setWidth(null); // Use default width from CSS
			}
		} else {
			// Save current dimensions before maximizing
			setPreviousDimensions({
				width: width,
				height: height ?? minHeight,
				x: position.x,
				y: position.y
			});
			// Maximize: move to top-left and set full width and height
			setPosition({ x: maxX, y: maxY });
			setWidth(maxWidth);
			setHeight(maxHeight);
		}
	};

	// Get resize cursor
	const getResizeCursor = (resizeDir: string | null): string => {
		if (!resizeDir) return 'default';
		const cursors: { [key: string]: string } = {
			nw: 'nwse-resize',
			ne: 'nesw-resize',
			sw: 'nesw-resize',
			se: 'nwse-resize',
			n: 'ns-resize',
			s: 'ns-resize',
			e: 'ew-resize',
			w: 'ew-resize'
		};
		return cursors[resizeDir] || 'default';
	};

	return {
		position,
		width,
		height,
		isDragging,
		isResizing,
		resizeDirection,
		snapHotspot,
		containerWidth,
		containerRef,
		handleMouseDown,
		handleDoubleClick,
		isMaximized: isMaximized(),
		getResizeDirection,
		getResizeCursor
	};
};
